/**
 * @flow
 */

import React from 'react';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { Modal, Header, Button, Icon } from 'semantic-ui-react';
import { FormattedMessage } from 'react-intl';
import type { ModalDialog, ServiceGroup } from '../../../../../type';

import { GET_SERVICE_GROUPS_QUERY } from '../ServicesList';

type DeleteServiceGroupDialogProps = {
    serviceGroup: ServiceGroup,
} & ModalDialog;

// Mutation that deletes specified service group
const DELETE_SERVICE_GROUP_MUTATION = gql`
    mutation deleteServiceGroup($input: DeleteServiceGroupInput!) {
        deleteServiceGroup(input: $input) {
            serviceGroupId
        }
    }
`;

const DeleteServiceGroupDialog = ({
    serviceGroup,
    trigger,
    open,
    onClose,
}: DeleteServiceGroupDialogProps) => {
    const salon = useSelector(({ user }) => user.get('salon'));
    const currentLanguage = useSelector(
        ({ intl }) => intl.get('locale') || intl.get('defaultLanguage'),
    );
    const [deleteServiceGroup] = useMutation(DELETE_SERVICE_GROUP_MUTATION);

    const onDelete = () => {
        const variables = { salon, lang: currentLanguage };
        return deleteServiceGroup({
            variables: {
                input: { serviceGroupId: serviceGroup.id },
            },
            // Optimistic response compensates network latency by removing the
            // group from the UI before the server responds.
            optimisticResponse: {
                __typename: 'Mutation',
                deleteServiceGroup: {
                    __typename: 'DeleteServiceGroupPayload',
                    serviceGroupId: serviceGroup.id,
                },
            },
            // Remove the deleted group from the cache (immutable rewrite —
            // readQuery results are frozen in AC3).
            update: (store, { data: { deleteServiceGroup: result } }) => {
                try {
                    const data = store.readQuery({
                        query: GET_SERVICE_GROUPS_QUERY,
                        variables,
                    });
                    if (!data) return;
                    store.writeQuery({
                        query: GET_SERVICE_GROUPS_QUERY,
                        variables,
                        data: {
                            ...data,
                            viewer: {
                                ...data.viewer,
                                salon: {
                                    ...data.viewer.salon,
                                    serviceGroups: {
                                        ...data.viewer.salon.serviceGroups,
                                        nodes: data.viewer.salon.serviceGroups.nodes.filter(
                                            sg =>
                                                sg.id !==
                                                result.serviceGroupId,
                                        ),
                                    },
                                },
                            },
                        },
                    });
                } catch (e) {
                    // It's safe to ignore this exception
                }
            },
        }).then(() => onClose());
    };

    return (
        <Modal trigger={trigger} open={open} onClose={onClose} size="small" basic>
        <Header icon>
            <Icon name="remove" />
            <FormattedMessage
                id="pages.settings.services.delete.deleteGroupHeader"
                defaultMessage="Delete Service Group"
            />
        </Header>
        <Modal.Content>
            <FormattedMessage
                id="pages.settings.services.delete.deleteGroupText"
                defaultMessage="Are you sure you want to delete {serviceGroupName} service group?"
                values={{
                    serviceGroupName: serviceGroup.name,
                }}
            />
        </Modal.Content>
        <Modal.Actions>
            <Button onClick={onClose} color="red" basic inverted>
                <Icon name="remove" />
                <FormattedMessage
                    id="pages.settings.services.delete.no"
                    defaultMessage="No"
                />
            </Button>
            <Button onClick={onDelete} color="green" inverted>
                <Icon name="checkmark" />
                <FormattedMessage
                    id="pages.settings.services.delete.yes"
                    defaultMessage="Yes"
                />
            </Button>
        </Modal.Actions>
        </Modal>
    );
};

export default DeleteServiceGroupDialog;
